import m from '../m.js'
import { api, toasts } from '../api.js'
import {
  annotateDeletions,
  annotateHunks,
  byTreeOrder,
  commitRuns,
  highlightHunks,
  languageOf,
  lineThreads,
  loadHighlighter,
  parsePatch,
  threadsByLine,
} from '../diff.js'
import { language, timeAgo } from '../i18n.js'
import { icon } from '../icons.js'
import { board } from '../store.js'
import { mergeButtonView } from './card.js'
import { avatarView, htmlView, pickerView } from './widgets.js'

// larger files start collapsed; larger patches are not highlighted (too slow)
const COLLAPSE_LINES = 400
const HIGHLIGHT_CHARS = 200_000
// files per annotation request (one GitHub GraphQL query)
const BLAME_BATCH = 10
// commit colors, see --commit-* in diff.styl
const COMMIT_COLORS = 8
// commit titles in the annotation column are cut here (and to the rows of their run)
const TITLE_CHARS = 100
// deleted lines are matched against each commit's own patch: one request per commit
const MAX_DELETION_COMMITS = 30

const SIGNS = { add: '+', del: '-', context: ' ' }

const short = sha => sha.slice(0, 7)

/**
 * The commit column of a run of lines: a colored bar, and the commit's title wrapped
 * over the run's rows (cut to them, and to TITLE_CHARS), linked to the commit.
 */
function commitCell(line, span, commits, prUrl) {
  const commit = line.commit && commits.get(line.commit)
  if (!commit) return m('td.diff-commit', { rowspan: span })
  const title =
    commit.title.length > TITLE_CHARS
      ? `${commit.title.slice(0, TITLE_CHARS - 1)}…`
      : commit.title
  return m.m(
    'td.diff-commit',
    {
      rowspan: span,
      vars: {
        '--commit-color': `var(--commit-${commit.index % COMMIT_COLORS})`,
        '--lines': span,
      },
    },
    m(
      'a',
      {
        href: `${prUrl}/commits/${line.commit}`,
        target: '_blank',
        title: [
          commit.title,
          `${short(line.commit)} · ${commit.author.login}${commit.date ? `, ${timeAgo(commit.date)}` : ''}`,
        ].join('\n'),
      },
      title
    )
  )
}

/** A review thread below its line; resolved ones start collapsed, like on GitHub. */
function threadView(thread) {
  return m('details.diff-thread', { key: thread.id, open: !thread.resolved }, [
    m('summary', [
      icon('comment'),
      m.t('diff.comments', thread.comments.length),
      thread.resolved && m('span.diff-thread-resolved', m.t('diff.resolved')),
    ]),
    thread.comments.map(c =>
      m('.diff-thread-comment', [
        m('.diff-thread-head', [
          avatarView({ user: c.author }),
          m('strong', c.author?.login || 'ghost'),
          m(
            'a.muted',
            {
              href: c.url,
              target: '_blank',
              title: new Date(c.createdAt).toLocaleString(language),
            },
            timeAgo(c.createdAt)
          ),
        ]),
        htmlView({ html: c.bodyHTML }),
      ])
    ),
  ])
}

/** GitHub's "Viewed" checkbox; DISMISSED: viewed before, changed since. */
function viewedView(state, onViewed) {
  return m('label.diff-viewed', [
    state === 'DISMISSED' && m('span.muted', m.t('diff.changedSinceViewed')),
    m('input[type=checkbox]', {
      checked: state === 'VIEWED',
      onchange: evt =>
        onViewed(evt.target.checked, evt.target.closest('.diff-file')),
    }),
    m.t('diff.viewed'),
  ])
}

function fileView({
  file,
  isCollapsed,
  toggle,
  commits,
  prUrl,
  viewed,
  onViewed,
  threads,
}) {
  const columns = commits ? 3 : 2
  // rows each commit cell spans, per hunk
  const runs = new Map(
    commits && file.hunks
      ? file.hunks.map(h => [h.lines, commitRuns(h.lines)])
      : []
  )
  const commentCount = [...(threads?.values() || [])]
    .flat()
    .reduce((sum, t) => sum + t.comments.length, 0)
  return m('.diff-file', { key: file.filename }, [
    m('.diff-file-head', [
      m(
        'button.diff-file-toggle',
        { onclick: toggle, 'aria-expanded': String(!isCollapsed) },
        [
          icon(isCollapsed ? 'chevron-right' : 'chevron-down'),
          m('span.diff-file-name', [
            file.previousFilename && [
              m('span.muted', file.previousFilename),
              ' → ',
            ],
            file.filename,
          ]),
          file.status !== 'modified' &&
            m('span.diff-file-status', m.t(`diff.status.${file.status}`)),
          m('.spacer'),
          commentCount > 0 &&
            m(
              'span.diff-file-comments',
              { title: m.t('diff.comments', commentCount) },
              [icon('comment'), commentCount]
            ),
          m('span.diff-additions', `+${file.additions}`),
          m('span.diff-deletions', `−${file.deletions}`),
        ]
      ),
      onViewed && viewedView(viewed, onViewed),
    ]),
    // own border: the header has its own, so it keeps its top line when stuck
    !isCollapsed &&
      m(
        '.diff-body',
        file.hunks
          ? m(
              'table.diff-table',
              m(
                'tbody',
                file.hunks.map(hunk => {
                  const lineThreadList = hunk.lines.map(l =>
                    lineThreads(l, threads)
                  )
                  // a line with threads takes two rows: the commit cell spans both
                  const rows = (from, span) =>
                    lineThreadList
                      .slice(from, from + span)
                      .reduce((sum, t) => sum + (t.length ? 2 : 1), 0)
                  return [
                    m('tr.diff-hunk', [
                      m('td.diff-num', { colspan: columns }),
                      m('td.diff-code', hunk.header),
                    ]),
                    hunk.lines.map((line, i, lines) => [
                      m(`tr.diff-${line.type}`, [
                        commits &&
                          runs.get(lines)[i] > 0 &&
                          commitCell(
                            line,
                            rows(i, runs.get(lines)[i]),
                            commits,
                            prUrl
                          ),
                        m('td.diff-num', line.old),
                        m('td.diff-num', line.new),
                        m('td.diff-code', [
                          m('span.diff-sign', SIGNS[line.type]),
                          line.html == null ? line.text : m.trust(line.html),
                        ]),
                      ]),
                      lineThreadList[i].length > 0 &&
                        m(
                          'tr.diff-threads',
                          m(
                            'td',
                            { colspan: 3 },
                            lineThreadList[i].map(threadView)
                          )
                        ),
                    ]),
                  ]
                })
              )
            )
          : m('.diff-empty.muted', m.t('diff.notShown'))
      ),
  ])
}

/** "All commits" or one of them; changes the files shown. */
function commitPicker(commits, selected, select) {
  const current = selected && commits.find(c => c.sha === selected)
  return pickerView({
    multi: false,
    align: 'left',
    className: 'diff-commit-picker',
    title: m.t('diff.pickCommit'),
    heading: m.t('diff.pickCommit'),
    selected: [selected ?? 'all'],
    options: [
      { value: 'all', label: m.t('diff.allCommits') },
      ...commits.map(c => ({
        value: c.sha,
        label: `${short(c.sha)} ${c.title}`,
        view: () => [m('code', short(c.sha)), c.title],
      })),
    ],
    // a click on the selected commit unselects it: back to all
    onchange: ([sha]) => select(sha && sha !== 'all' ? sha : null),
    toggle: [
      icon('git-commit'),
      current
        ? [m('code', short(current.sha)), ` ${current.title}`]
        : m.t('diff.allCommits'),
      icon('triangle-down'),
    ],
  })
}

/**
 * The changed files of a pull request with their diffs, read only, and the merge button
 * at the top and the end. All commits at once, with the commit that last changed each
 * added line beside it and the review comments below their lines, or one commit's
 * changes. Reloads when the PR's head commit changes (a push, through the detail
 * view's live reload).
 */
export const diffView = m.withState(() => {
  let loadedHead = null
  let number = null
  // PR commits, oldest first: list, and sha → { ...commit, index } for the annotations
  let commits = null
  let commitsBySha = null
  let selected = null // a commit's sha, or null for all
  const views = new Map() // 'all' or sha → { files, error }
  const collapsed = new Set()
  const blamed = new Set() // files asked for annotations at loadedHead
  let deletionsLoaded = null // head the deleted lines were annotated for
  // the user's Viewed marks on GitHub: path → VIEWED | UNVIEWED | DISMISSED
  let viewed = null
  let viewedCollapsed = false // viewed files collapse once, when the tab opens
  let threads = null // review threads: path → line → threads, see threadsByLine
  let threadsFor = null // the item they were loaded for: a live reload brings a fresh one

  const viewKey = () => selected ?? 'all'

  const startsCollapsed = f =>
    (f.status === 'removed' || f.additions + f.deletions > COLLAPSE_LINES) &&
    viewed?.get(f.filename) !== 'DISMISSED'

  async function loadFiles(key, url, head) {
    try {
      const list = await api('GET', board.url(url))
      if (head !== loadedHead) return
      const first = !views.get(key)?.files
      const files = list
        .map(f => ({ ...f, hunks: f.patch ? parsePatch(f.patch) : null }))
        .sort((a, b) => byTreeOrder(a.filename, b.filename))
      if (first)
        files.filter(startsCollapsed).forEach(f => collapsed.add(f.filename))
      views.set(key, { files, error: null })
      m.redraw()
      if (key === 'all') {
        loadBlame(head)
        loadDeletions(head)
      }
      // shown plain right away, colored once highlight.js has loaded
      const hljs = await loadHighlighter()
      if (head !== loadedHead) return
      for (const f of files) {
        const fileLanguage = languageOf(f.filename, hljs)
        if (f.hunks && fileLanguage && f.patch.length <= HIGHLIGHT_CHARS)
          highlightHunks(f.hunks, fileLanguage, hljs)
      }
    } catch (err) {
      if (head === loadedHead)
        views.set(key, { ...views.get(key), error: err.message })
    }
    m.redraw()
  }

  async function loadCommits(head) {
    try {
      const list = await api('GET', board.url(`/pulls/${number}/commits`))
      if (head !== loadedHead) return
      commits = list
      commitsBySha = new Map(list.map((c, index) => [c.sha, { ...c, index }]))
      loadBlame(head)
      loadDeletions(head)
    } catch {
      // without the list there is no picker and no annotations, the diff still shows
    }
    m.redraw()
  }

  /**
   * Deleted lines: each PR commit's own patch says which lines it deleted (see
   * annotateDeletions). Merge commits are left out, they mostly carry the base's changes.
   */
  async function loadDeletions(head) {
    const files = views.get('all')?.files
    const own = commits?.filter(c => !c.merge)
    if (!files || !own || commits.length < 2 || deletionsLoaded === head) return
    if (own.length > MAX_DELETION_COMMITS) return
    deletionsLoaded = head
    try {
      const patches = await Promise.all(
        own.map(c => api('GET', board.url(`/commits/${c.sha}/files`)))
      )
      if (head !== loadedHead) return
      for (const file of files) {
        if (!file.hunks || !file.deletions) continue
        // the file under its name, or its earlier one in commits before a rename
        const names = [file.filename, file.previousFilename].filter(Boolean)
        const commitHunks = own.flatMap((c, i) => {
          const patch = patches[i].find(f =>
            [f.filename, f.previousFilename].some(n => n && names.includes(n))
          )?.patch
          return patch ? [{ sha: c.sha, hunks: parsePatch(patch) }] : []
        })
        annotateDeletions(file.hunks, commitHunks)
      }
    } catch {
      // deleted lines stay unannotated
    }
    m.redraw()
  }

  async function loadViewed(head) {
    try {
      const marks = await api('GET', board.url(`/pulls/${number}/viewed`))
      if (head !== loadedHead) return
      viewed = new Map(Object.entries(marks))
      // viewed files start collapsed; changed since being viewed, they open again,
      // also when a push changes one while the diff is open
      for (const [path, state] of viewed) {
        if (state === 'DISMISSED') collapsed.delete(path)
        else if (state === 'VIEWED' && !viewedCollapsed) collapsed.add(path)
      }
      viewedCollapsed = true
      if (!selected) loadBlame(head)
    } catch {
      // no checkboxes; the diff works without them
    }
    m.redraw()
  }

  async function loadThreads(head) {
    try {
      const list = await api('GET', board.url(`/pulls/${number}/threads`))
      if (head !== loadedHead) return
      threads = threadsByLine(list)
    } catch {
      // the diff without its review comments
    }
    m.redraw()
  }

  /** Marks a file (un)viewed on GitHub; viewed files collapse, like there. */
  async function setViewed(path, isViewed, fileEl) {
    const before = viewed.get(path)
    viewed.set(path, isViewed ? 'VIEWED' : 'UNVIEWED')
    if (isViewed) {
      collapsed.add(path)
      m.redraw.sync()
      keepInView(fileEl)
    } else toggle(path, true)
    try {
      await api('PUT', board.url(`/pulls/${number}/viewed`), {
        path,
        viewed: isViewed,
      })
    } catch (err) {
      viewed.set(path, before)
      toasts.show(m.t('diff.viewedFailed', { error: err.message }))
    }
    m.redraw()
  }

  /**
   * After collapsing a file read from within: its header went up out of view, the
   * scroll position stayed. Like GitHub, scroll back to it, below the sticky summary
   * (scroll-margin-top in diff.styl), so the next file follows right after.
   */
  function keepInView(fileEl) {
    const scroller = fileEl?.closest('.detail-body')
    if (!scroller || !fileEl.scrollIntoView) return
    const summary = scroller?.querySelector('.diff-summary')
    const visibleTop =
      scroller.getBoundingClientRect().top + (summary?.offsetHeight || 0)
    if (fileEl.getBoundingClientRect().top < visibleTop)
      fileEl.scrollIntoView({ block: 'start' })
  }

  /** Annotations for the expanded files of all commits not asked for yet. */
  async function loadBlame(head) {
    const files = views.get('all')?.files
    if (!files || !commits || commits.length < 2) return
    const paths = files
      .filter(
        f =>
          f.hunks &&
          f.status !== 'removed' &&
          !collapsed.has(f.filename) &&
          !blamed.has(f.filename)
      )
      .map(f => f.filename)
    paths.forEach(p => blamed.add(p))
    const shas = new Set(commitsBySha.keys())
    for (let i = 0; i < paths.length; i += BLAME_BATCH) {
      try {
        const ranges = await api('POST', board.url('/blame'), {
          head,
          paths: paths.slice(i, i + BLAME_BATCH),
        })
        if (head !== loadedHead) return
        for (const file of files)
          if (ranges[file.filename])
            annotateHunks(file.hunks, ranges[file.filename], shas)
        m.redraw()
      } catch {
        // no annotations for these files; the diff is complete without them
      }
    }
  }

  function toggle(filename, expand = collapsed.has(filename)) {
    if (expand) {
      collapsed.delete(filename)
      if (!selected) loadBlame(loadedHead)
    } else collapsed.add(filename)
  }

  function select(sha) {
    selected = sha
    if (sha && !views.has(sha)) {
      views.set(sha, { files: null, error: null })
      loadFiles(sha, `/commits/${sha}/files`, loadedHead)
    }
  }

  /** Everything for a new head commit, the first one included: files, commits, Viewed marks. */
  function loadHead(item) {
    loadedHead = item.pr.head
    number = item.number
    views.clear()
    blamed.clear()
    // a commit gone after a force push: back to all
    if (selected && commits && !commits.some(c => c.sha === selected))
      selected = null
    views.set('all', { files: null, error: null })
    loadFiles('all', `/pulls/${number}/files`, loadedHead)
    loadCommits(loadedHead)
    loadViewed(loadedHead)
    if (selected) select(selected)
  }

  /** The bar above the files: commit picker, counts, GitHub link and merge button. */
  const summaryView = ({ item, files, marks, merge }) => {
    const viewedCount =
      marks && files?.filter(f => marks.get(f.filename) === 'VIEWED').length
    return m('.diff-summary', [
      commits?.length > 1 && commitPicker(commits, selected, select),
      files &&
        m('span', [
          m.t('diff.files', files.length),
          // unknown on boards saved before the count was loaded
          !selected &&
            item.pr.commits != null && [
              ' · ',
              m.t('diff.commits', item.pr.commits),
            ],
        ]),
      m('.spacer'),
      marks &&
        files &&
        m(
          'span.diff-viewed-count',
          m.t('diff.viewedCount', { viewed: viewedCount, total: files.length })
        ),
      m(
        'a',
        {
          href: selected
            ? `${item.url}/commits/${selected}`
            : `${item.url}/changes`,
          target: '_blank',
        },
        m.t('diff.viewOnGithub')
      ),
      merge && mergeButtonView(merge),
    ])
  }

  return ({ item, repo, onmerged }) => {
    const merge = repo &&
      item.state === 'open' && { card: item, repo, onmerged }
    const isNewHead = item.pr.head !== loadedHead
    if (isNewHead) loadHead(item)
    // review comments come with live reloads of the detail view (webhooks)
    if (isNewHead || item !== threadsFor) {
      threadsFor = item
      loadThreads(loadedHead)
    }
    const { files, error } = views.get(viewKey()) || {}
    const annotated = !selected && commits?.length > 1 ? commitsBySha : null
    // marks belong to the PR's files: shown with all commits
    const marks = !selected && viewed
    return m('.diff', [
      summaryView({ item, files, marks, merge }),
      error && !files && m('.error', error),
      !files && !error && m('.muted.center', m.t('diff.loading')),
      files?.length === 0 && m('.diff-empty.muted', m.t('diff.none')),
      files?.map(file =>
        fileView({
          file,
          isCollapsed: collapsed.has(file.filename),
          toggle: () => toggle(file.filename),
          commits: annotated,
          prUrl: item.url,
          viewed: marks && marks.get(file.filename),
          onViewed:
            marks && ((value, el) => setViewed(file.filename, value, el)),
          // on the PR's diff: a commit's own lines are numbered differently
          threads: selected ? null : threads?.get(file.filename),
        })
      ),
      // reviewed to the end: merge from here
      files && merge && m('.diff-foot', mergeButtonView(merge)),
    ])
  }
})
